import type { LocationQuery, LocationQueryValue } from 'vue-router';
import {
  useMarketingTrackingStore,
  type AptAttribution,
  type FacebookClickConversion,
  type GoogleClickConversion,
  type MarketingUtm,
  type TiktokClickConversion,
} from '~/stores/apostatudo/marketing-tracking';
import isDeepEqual from '~/utils/apostatudo/is-deep-equal';

const normalize = (
  value?: LocationQueryValue | LocationQueryValue[],
): string | undefined => {
  const raw = Array.isArray(value) ? value[0] : value;

  return raw == null || raw === '' ? undefined : String(raw);
};

const toLowerCaseQuery = (query: LocationQuery): LocationQuery =>
  Object.fromEntries(
    Object.entries(query).map(([key, value]) => [key.toLowerCase(), value]),
  );

const hasAnyValue = (values: Record<string, string | undefined>) =>
  Object.values(values).some((value) => value !== undefined);

export const useMarketingTracking = () => {
  const store = useMarketingTrackingStore();
  // Dentro do app, não capturamos identificadores de terceiros (ENG-397).
  const { isNativeApp } = useAppTrackingPolicy();

  const persistFromQuery = (rawQuery: LocationQuery) => {
    const query = toLowerCaseQuery(rawQuery);

    const apt: AptAttribution = {
      channel: normalize(query.apt_channel),
      content: normalize(query.apt_content),
      extra: normalize(query.apt_extra),
    };

    // APT atua como identificador principal de campanha. Quando muda,
    // limpa utm/google/facebook/tiktok antes de aplicar a nova query.
    if (hasAnyValue(apt) && !isDeepEqual(apt, store.apt)) {
      store.resetTracking();
    }

    const utm: MarketingUtm = {
      id: normalize(query.utm_id),
      term: normalize(query.utm_term),
      medium: normalize(query.utm_medium),
      source: normalize(query.utm_source),
      content: normalize(query.utm_content),
      campaign: normalize(query.utm_campaign),
    };

    if (hasAnyValue(utm)) store.setUtm(utm);

    // Click-ids de terceiros (Google/Facebook/TikTok) só no navegador.
    // Dentro do app não são capturados (separação app/site, ENG-397).
    const google: GoogleClickConversion = isNativeApp
      ? {}
      : {
          gclid: normalize(query.gclid),
          gbraid: normalize(query.gbraid),
          wbraid: normalize(query.wbraid),
        };

    if (hasAnyValue(google)) store.setGoogle(google);

    const facebook: FacebookClickConversion = isNativeApp
      ? {}
      : {
          fbclid: normalize(query.fbclid),
        };

    if (hasAnyValue(facebook)) store.setFacebook(facebook);

    const tiktok: TiktokClickConversion = isNativeApp
      ? {}
      : {
          ttclid: normalize(query.ttclid),
        };

    if (hasAnyValue(tiktok)) store.setTiktok(tiktok);

    if (hasAnyValue(apt)) store.setApt(apt);

    // Renova a janela de atribuição (48h) sempre que a URL trouxer sinal
    // de campanha. Touches sem query (cookies, navegação interna) não
    // renovam — mantém semântica de "last campaign touch", não sliding.
    const hasCampaignSignal =
      hasAnyValue(apt) ||
      hasAnyValue(utm) ||
      hasAnyValue(google) ||
      hasAnyValue(facebook) ||
      hasAnyValue(tiktok);

    if (hasCampaignSignal) store.markAcquired();
  };

  const watchCookies = () => {
    // No app não há pixel FB, logo não há `_fbp`/`_fbc` a ler (ENG-397).
    if (isNativeApp) {
      return;
    }

    const fbc = useCookie('_fbc', { watch: true });
    const fbp = useCookie('_fbp', { watch: true });

    watch(
      [fbc, fbp],
      ([fbcValue, fbpValue]) => {
        store.setFacebook({
          fbc: fbcValue ?? undefined,
          fbp: fbpValue ?? undefined,
        });
      },
      { immediate: true },
    );
  };

  return {
    persistFromQuery,
    watchCookies,
  };
};
